import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ContractAlertTargetResource } from "@shared/interfaces";
import { Separator } from "@/components/ui/separator";
import { useFlow } from "@/contexts/FlowContext.tsx";
import { FlowPageInterface } from "@/interfaces";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { AddOrEditContactModal } from "./dialogs/AddOrEditContactDialog";
import { useDeleteContractAlertTarget } from "@/hooks/alerts/useDeleteContractAlertTarget";
import { useContractAlertTargets } from "@/hooks/alerts/useContractAlertTargets";
import AlertsScheduleNotice from "@/components/alerts-schedule-notice";
import { formatPhone } from "@/utils/phone";

export const FlowPageAlerts = (props: FlowPageInterface) => {
  const { t } = useTranslation();
  const { contractId } = useFlow();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setEditIndex(null);
    }
  }, [isOpen]);

  const {
    data: alertsData,
    refetch,
    isFetching: isAlertsFetching,
  } = useContractAlertTargets(contractId);

  const noDataAlerts =
    (alertsData && alertsData.length === 0) || isAlertsFetching;

  if (!props.currentlyVisible) {
    return <></>;
  }

  return (
    <div className={"max-w-6xl mx-auto"}>
      <div className={"follo-label text-card-foreground"}>
        {t("fe.pages.flow.steps.alerts.title")}
      </div>
      <div className="text-sm font-normal text-muted-foreground leading-5 my-3">
        <div className={"mb-2 md:max-w-[700px]"}>
          {t("fe.pages.flow.steps.alerts.description1")}
        </div>
        <div className="md:max-w-[700px] mb-2">
          {t("fe.pages.flow.steps.alerts.description2")}
        </div>
      </div>
      <AlertsScheduleNotice />
      <div className={"follo-card mt-7"}>
        <div className={""}>
          <div
            className={
              "flex justify-between items-center border-zinc-200 border-b-1 px-7 py-5"
            }
          >
            <div>
              <h3 className="follo-label">
                {t("fe.pages.flow.steps.alerts.table.title")}
              </h3>
              {/* TODO This is for the next feature (implement later) */}
              {/* <p className="text-sm text-muted-foreground mt-1">
                {t("fe.pages.flow.steps.alerts.table.description", {
                  numberOfAlerts: 2, // FIXME ADD MAX ALERTS NUMBER (NEED FROM BACKEND)
                })}
              </p> */}
            </div>
            <Button
              onClick={() => setIsOpen(true)}
              className="bg-white border shadow-xs cursor-pointer"
              variant="ghost"
            >
              <Plus />
              <span className="ml-0.5">
                {t("fe.pages.flow.steps.alerts.table.addContactButton")}
              </span>
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-6 min-w-[150px] text-muted-foreground font-medium text-sm">
                  {t("fe.pages.flow.steps.alerts.table.headings.method")}
                </TableHead>
                <TableHead className="px-6 min-w-[150px] text-muted-foreground font-medium text-sm">
                  {t("fe.pages.flow.steps.alerts.table.headings.contact")}
                </TableHead>
                <TableHead className="px-6 min-w-[150px] text-muted-foreground font-medium text-sm">
                  {t("fe.pages.flow.steps.alerts.table.headings.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {noDataAlerts ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {t("fe.pages.flow.steps.alerts.table.emptyState")}
                  </TableCell>
                </TableRow>
              ) : (
                (alertsData ?? []).map(
                  (row: ContractAlertTargetResource, index: number) => {
                    return (
                      <TableRow key={row.id}>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground gap-2">
                           {t(
                              `models.alertContractModel.alerts.options.${row.attributes.method.toLowerCase()}`
                            )}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {row.attributes.method === "EMAIL"
                            ? row.attributes.target
                            : formatPhone(row.attributes.target)}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {row.id && (
                            <ActionsCell
                              id={row.id}
                              index={index}
                              setEditIndex={setEditIndex}
                              onOpen={() => setIsOpen(true)}
                              isFetching={isAlertsFetching}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }
                )
              )}
            </TableBody>
          </Table>
        </div>
        {/* Confirm only when there is a step to confirm *into*. With no payment plan
            agreed yet the confirmation step is locked, and advancing onto it bounced the
            customer straight back to the payment agreement. */}
        {props.hasNextStep && (
          <>
            <Separator className="" />
            <div className={"simple-card-footer text-right py-4 px-7"}>
              <Button
                className="cursor-pointer"
                onClick={() => props.nextStep()}
              >
                {t("ui.confirm")}
              </Button>
            </div>
          </>
        )}
      </div>
      {isOpen && (
        <AddOrEditContactModal
          row={
            alertsData && editIndex !== null && editIndex >= 0
              ? alertsData[editIndex]
              : null
          }
          open={isOpen}
          onOpenChange={setIsOpen}
          contractId={contractId}
          refetch={refetch}
        />
      )}
    </div>
  );
};

interface ActionsCellProps {
  id: number;
  index: number;
  setEditIndex: Dispatch<SetStateAction<number | null>>;
  onOpen: () => void;
  isFetching: boolean;
}

const ActionsCell = ({
  id,
  index,
  setEditIndex,
  onOpen,
  isFetching,
}: ActionsCellProps) => {
  const { mutate: deleteAlert, isPending: deleting } =
    useDeleteContractAlertTarget();

  return (
    <div className={"action-cell flex justify-start gap-3"}>
      <Button
        variant="ghost"
        size="icon-sm"
        disabled={deleting || isFetching}
        onClick={() => {
          setEditIndex(index);
          onOpen();
        }}
        className="cursor-pointer"
      >
        <Pencil size={18} className="text-primary cursor-pointer" />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        disabled={deleting || isFetching}
        onClick={() => deleteAlert(id)}
        className="cursor-pointer"
      >
        <Trash2 size={18} className="text-destructive cursor-pointer" />
      </Button>
    </div>
  );
};
